import { PerimetersService } from '../../../@core/utils/perimeters.service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GridApi, GridOptions } from 'ag-grid-enterprise';
import { first, Observable, Subscription } from 'rxjs';
import { ChartsService, SpreadsheetColumnsService, UsersService } from '../../../@core/utils';
import { KPI } from '../../../@core/api';
import { EmissionFactorService } from '../../../@core/utils/emission-factor.service';
import { TABLE_FILTER_ICON } from '../../../@core/utils/constants';
import { AgGridAngular } from 'ag-grid-angular';
import { GROUP_PERMISSION_EDIT_ENERGY_SETTINGS } from '../../../@core/api/models';

@Component({
    selector: 'app-ag-grid-emission-factors',
    templateUrl: './ag-grid-emission-factors.component.html',
    styleUrls: ['./ag-grid-emission-factors.component.scss'],
    standalone: true,
    imports: [AgGridAngular]
})
export class AgGridEmissionFactorsComponent implements OnInit, OnDestroy {

  constructor(
    private translate: TranslateService,
    private spreadsheetColumnsService: SpreadsheetColumnsService,
    private emissionFactorService: EmissionFactorService,
    private chartService: ChartsService,
    private perimetersService: PerimetersService,
    private usersService: UsersService,
  ) { }

  @Input() public energyTypeId: number;
  @Input() public providerId: number;
  @Input() public emissionFactorByCountryAndYear: KPI.EmissionValueByCountryAndYear[];
  @Input() public emissionFactorByYear: KPI.EmissionValueByYear = {};
  @Input() public based: 'LOCATION' | 'MARKET';

  public years = Array.from({ length: 31 }, (_, i) => String(2020 + i));
  public canEdit$: Observable<boolean> = this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_ENERGY_SETTINGS]);

  private subscriptions: Subscription[] = [];

  public loading: boolean = false;
  public gridOptions: GridOptions = {};
  private gridApi: GridApi;
  private minEmissionFactor: number = 0;
  private maxEmissionFactor: number = 1000;

  ngOnInit() {
    this.canEdit$.pipe(first()).subscribe((canEdit) => {
      this.gridOptions = {
        ...this.gridOptions,
        suppressContextMenu: true,
        loading: false,
        context: this,
        rowHeight: 28,
        rowBuffer: 10, // Draw 10 rows above and below the visible rows (Performance)
        allowDragFromColumnsToolPanel: true, // Allow user to move columns from panel
        cellSelection: {
          suppressMultiRanges: true,
        },
        rowSelection: 'multiple', // Enable to select the entire row instead of a cell
        defaultColDef: {
          sortable: true,
          resizable: true,
          editable: false,
          filter: true,
          icons: {
            filter: TABLE_FILTER_ICON,
          },
          enableCellChangeFlash: true, // Flash cell when changed
          menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
        },
        // sideBar: false,
        columnDefs: [], // Columns definition (Set it later)
        animateRows: false, // Row animations (when expanding)
        // Always show the column menus
        suppressMenuHide: true,
        groupHeaderHeight: 0, // Hide the columns groups in the header (Set it to 20 to show)
        getRowId: params => params.data?.id?.toString(),
        suppressPropertyNamesCheck: true,
            columnMenu: 'new',
        // Restrict aggregations some functions (sum in an ag-grid native one)
        onGridReady: (params) => {
          this.gridApi = params.api;
          // Load columns
          this.gridApi.setGridOption(
            'columnDefs',
            [
              {
                field: 'year',
                headerName: this.translate.instant('Year'),
              },
              ...this.years.map((year) => ({
                field: String(year),
                headerName: String(year),
                editable: this.based === 'MARKET' && canEdit,
                width: 80,
                filter: false,
                menuTabs: [],
                sortable: false,
                cellEditor: 'agNumberCellEditor',
                cellEditorParams: {
                  min: this.minEmissionFactor,
                  max: this.maxEmissionFactor,
                  precision: 0,
                },
                valueFormatter: this.spreadsheetColumnsService.nullFormatter,
              })),
            ],
          );
          // Load data
          this.loadAgGridData().then((rowData) => {
            if (this.gridApi) {
              this.gridApi.setGridOption('rowData', rowData); // Load data rows
            }
          });
        },
        // Hook executed after first data set
        onFirstDataRendered: () => {
          this.loading = false; // End the loading spinner
          this.subscriptions.push(
            this.perimetersService.perimeters$.subscribe((perimeters) => {
              this.gridApi?.getColumnFilterInstance('name').then((filterInstance) => {
                if (filterInstance) {
                  // Apply the set of values as a filter to the column
                  filterInstance.setModel({
                    filterType: 'set',
                    values: perimeters.map((perimeter) => perimeter.name),
                  });
                  this.gridApi?.onFilterChanged();
                }
              });
            }),
          );
        },
        onCellValueChanged: (params) => {
          if (
            params.newValue > this.minEmissionFactor
            && params.newValue !== null
            && params.newValue < this.maxEmissionFactor
          ) {
            this.updateProviderEmissionFactor(Number(params.colDef.field), Number(params.newValue / 1000));
          } else {
            params.node.setDataValue(params.colDef.field, Number(params.oldValue));
          }
        }
      };
    });
    this.subscriptions.push(
      this.spreadsheetColumnsService.getAgGridTranslations().subscribe((agGridTranslations) => {
        this.gridOptions.localeText = agGridTranslations;
      }),
    );
  }

  private loadAgGridData(): Promise<any> {
    /**
     * Postponement data
     */
    return new Promise((resolve) => {
      if (this.based === 'LOCATION') {
        const rows = this.emissionFactorByCountryAndYear.map((country) => {
          const row = {
            id: country.country?.id,
            year: country.country?.name
              + ' - '
              + this.translate.instant('g eq CO2 / kWh'),
          };
          this.years.map((year) => row[year] = country.data[year])
          return row;
        });
        resolve(rows);
      } else {
        const row = {
          year: this.translate.instant('g eq CO2 / kWh'),
          ...this.emissionFactorByYear
        };
        resolve([row]);
      }
    });
  }

  private updateProviderEmissionFactor(year: number, factor: number): void {
    this.subscriptions.push(
      this.emissionFactorService.updateProviderEmissionFactor(
        this.providerId,
        this.energyTypeId,
        year,
        factor,
      ).subscribe(() => this.chartService._carbonEmissionsHasChanged$.next()),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
